import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { MdsAddLink, MdsExpandMoreFillXs, MdsImageXs, MdsUpload } from "@sinch/icons";
import React, { ChangeEvent, FC, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "../../../MdsIcon/MdsIcon";
import { ToolbarButton } from "../components/ToolbarButton";
import { INSERT_IMAGE_COMMAND, InsertImagePayload } from "../plugins/ImagesPlugin";
import { InsertImageUrlDialog } from "./InsertImageUrlDialog";

export const InsertImage: FC = () => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleUploadClick = useCallback(() => {
    setAnchorEl(null);
    // Must stay synchronous - the file picker needs the user activation of this click.
    inputRef.current?.click();
  }, []);

  const handleUrlClick = useCallback(() => {
    setAnchorEl(null);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleUrlConfirm = useCallback(
    (payload: InsertImagePayload) => {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
      setDialogOpen(false);
    },
    [editor]
  );

  const handleFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset the input so picking the same file again still fires onChange.
      event.target.value = "";

      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          altText: file.name,
          src: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
    },
    [editor]
  );

  const disabled = !editor.isEditable();

  return (
    <div>
      <Tooltip
        placement="top"
        slotProps={{ popper: { disablePortal: true } }}
        title={open ? "" : t("TextEditor.insertImage")}
      >
        <ToolbarButton
          disabled={disabled}
          endIcon={<MdsIcon fontSize="xs" icon={MdsExpandMoreFillXs} />}
          onClick={handleOpen}
          tabIndex={-1}
          value="image"
        >
          <MdsIcon fontSize="xs" icon={MdsImageXs} />
        </ToolbarButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} disablePortal onClose={handleClose} open={open}>
        <MenuItem disabled={disabled} onClick={handleUploadClick}>
          <ListItemIcon>
            <MdsIcon fontSize="small" icon={MdsUpload} />
          </ListItemIcon>
          <ListItemText>{t("TextEditor.uploadImage")}</ListItemText>
        </MenuItem>
        <MenuItem disabled={disabled} onClick={handleUrlClick}>
          <ListItemIcon>
            <MdsIcon fontSize="small" icon={MdsAddLink} />
          </ListItemIcon>
          <ListItemText>{t("TextEditor.insertImageFromUrl")}</ListItemText>
        </MenuItem>
      </Menu>
      <input ref={inputRef} accept="image/*" onChange={handleFile} style={{ display: "none" }} type="file" />
      <InsertImageUrlDialog onClose={handleDialogClose} onConfirm={handleUrlConfirm} open={dialogOpen} />
    </div>
  );
};
